import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HotelIcon from '@mui/icons-material/Hotel';
import BathroomIcon from '@mui/icons-material/Bathroom';
import WeekendIcon from '@mui/icons-material/Weekend';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import InsightsIcon from '@mui/icons-material/Insights';
import { getHousekeepingSettings, updateHousekeepingSettings } from '../../services/api';
import { detectThemeSync } from '../../utils/themeUtils';
import {
  getHousekeepingListPaperSx,
  getHousekeepingThemeColors,
  HOUSEKEEPING_ACCENT,
} from '../../utils/housekeepingMobileLayout';
import { getSoftFieldSx } from './housekeepingFormKit';

const CATEGORIES = [
  { id: 'bedroom', label: 'Bedroom', icon: HotelIcon },
  { id: 'bathroom', label: 'Bathroom', icon: BathroomIcon },
  { id: 'living', label: 'Living / amenities', icon: WeekendIcon },
];

const STARTER_ITEMS = [
  { label: 'Make bed and change linens', category: 'bedroom', mandatory: true },
  { label: 'Dust surfaces and furniture', category: 'bedroom', mandatory: true },
  { label: 'Vacuum / mop floor', category: 'bedroom', mandatory: true },
  { label: 'Clean toilet and sanitize seat', category: 'bathroom', mandatory: true },
  { label: 'Wipe shower, sink, and mirror', category: 'bathroom', mandatory: true },
  { label: 'Restock towels and toiletries', category: 'bathroom', mandatory: true },
  { label: 'Empty bins and replace liners', category: 'living', mandatory: true },
  { label: 'Check minibar / tea tray', category: 'living', mandatory: false },
  { label: 'Set AC and lights for arrival', category: 'living', mandatory: true },
];

const newItemId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `chk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const normalizeItems = (raw, defaultMandatory = false) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === 'string') {
        return { id: newItemId(), label: item.trim(), category: 'bedroom', mandatory: defaultMandatory };
      }
      const label = String(item?.label || '').trim();
      if (!label) return null;
      const category = CATEGORIES.some((c) => c.id === item.category) ? item.category : 'bedroom';
      return {
        id: String(item.id || newItemId()),
        label,
        category,
        mandatory: typeof item.mandatory === 'boolean' ? item.mandatory : defaultMandatory,
      };
    })
    .filter(Boolean);
};

const itemUsageCount = (item, usage) => {
  const byId = Number(usage?.by_id?.[item.id] || 0);
  const byLabel = Number(usage?.by_label?.[String(item.label || '').trim().toLowerCase()] || 0);
  return Math.max(byId, byLabel);
};

export default function HousekeepingConfigSection() {
  const [isDark, setIsDark] = useState(() => detectThemeSync() === 'dark');
  const colors = getHousekeepingThemeColors(isDark);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [enabledSaving, setEnabledSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [usage, setUsage] = useState({
    cleans_with_checks: 0,
    total_item_checks: 0,
    by_id: {},
    by_label: {},
  });
  const [drafts, setDrafts] = useState({ bedroom: '', bathroom: '', living: '' });
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  useEffect(() => {
    const sync = () => setIsDark(detectThemeSync() === 'dark');
    window.addEventListener('themechange', sync);
    return () => window.removeEventListener('themechange', sync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getHousekeepingSettings();
        if (cancelled) return;
        const loadedItems = normalizeItems(data?.cleaning_checklist, Boolean(data?.checklist_mandatory));
        setItems(loadedItems);
        setEnabled(Boolean(data?.checklist_enabled) && loadedItems.length > 0);
        if (data?.checklist_usage) setUsage(data.checklist_usage);
      } catch {
        if (!cancelled) {
          setSnack({ open: true, severity: 'error', message: 'Could not load housekeeping config.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const persistItems = useCallback(async (nextItems, nextEnabled = enabled) => {
    const canEnable = nextItems.length > 0 && nextEnabled;
    setItems(nextItems);
    if (!canEnable) setEnabled(false);
    setSaving(true);
    try {
      const data = await updateHousekeepingSettings({
        checklist_enabled: canEnable,
        checklist_mandatory: canEnable && nextItems.some((item) => item.mandatory),
        cleaning_checklist: nextItems,
      });
      const savedItems = normalizeItems(data?.cleaning_checklist, Boolean(data?.checklist_mandatory));
      setItems(savedItems);
      setEnabled(Boolean(data?.checklist_enabled) && savedItems.length > 0);
      if (data?.checklist_usage) setUsage(data.checklist_usage);
    } catch {
      setSnack({ open: true, severity: 'error', message: 'Could not save checklist step.' });
    } finally {
      setSaving(false);
    }
  }, [enabled]);

  const addItem = useCallback((category, label) => {
    const text = String(label || '').trim();
    if (!text) return;
    const next = [...items, { id: newItemId(), label: text, category, mandatory: true }];
    setDrafts((prev) => ({ ...prev, [category]: '' }));
    persistItems(next);
  }, [items, persistItems]);

  const addStarterPack = useCallback(() => {
    const existing = new Set(items.map((item) => item.label.toLowerCase()));
    const extras = STARTER_ITEMS
      .filter((item) => !existing.has(item.label.toLowerCase()))
      .map((item) => ({ ...item, id: newItemId(), mandatory: item.mandatory !== false }));
    if (!extras.length) return;
    persistItems([...items, ...extras]);
  }, [items, persistItems]);

  const removeItem = useCallback((itemId) => {
    persistItems(items.filter((row) => row.id !== itemId));
  }, [items, persistItems]);

  const moveItem = useCallback((itemId, direction) => {
    const index = items.findIndex((item) => item.id === itemId);
    if (index < 0) return;
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const next = [...items];
    const [moved] = next.splice(index, 1);
    next.splice(nextIndex, 0, moved);
    persistItems(next);
  }, [items, persistItems]);

  const toggleMandatory = useCallback((itemId) => {
    persistItems(items.map((row) => (
      row.id === itemId ? { ...row, mandatory: !row.mandatory } : row
    )));
  }, [items, persistItems]);

  const handleEnableToggle = useCallback(async (event) => {
    const nextEnabled = event.target.checked;
    if (nextEnabled && items.length === 0) {
      setEnabled(false);
      setSnack({
        open: true,
        severity: 'info',
        message: 'Add at least one checklist step before turning this on.',
      });
      return;
    }
    const previous = enabled;
    setEnabled(nextEnabled);
    setEnabledSaving(true);
    try {
      const data = await updateHousekeepingSettings({
        checklist_enabled: nextEnabled,
        checklist_mandatory: nextEnabled && items.some((item) => item.mandatory),
      });
      setEnabled(Boolean(data?.checklist_enabled));
      setSnack({
        open: true,
        severity: 'success',
        message: nextEnabled ? 'Checklist is on for room cleanings.' : 'Checklist is off. It will not show on room cleanings.',
      });
    } catch {
      setEnabled(previous);
      setSnack({ open: true, severity: 'error', message: 'Could not update checklist setting.' });
    } finally {
      setEnabledSaving(false);
    }
  }, [enabled, items]);

  const requiredCount = items.filter((item) => item.mandatory).length;
  const usageRows = useMemo(
    () => items
      .map((item) => ({ ...item, usageCount: itemUsageCount(item, usage) }))
      .sort((a, b) => b.usageCount - a.usageCount),
    [items, usage]
  );
  const topUsed = usageRows.filter((item) => item.usageCount > 0).slice(0, 3);
  const statusLabel = useMemo(() => {
    if (!enabled) return 'Checklist off';
    if (!requiredCount) return 'All steps optional';
    return `${requiredCount} required step${requiredCount === 1 ? '' : 's'}`;
  }, [enabled, requiredCount]);

  const cardSx = {
    ...getHousekeepingListPaperSx(isDark),
    p: { xs: 2, sm: 2.5 },
    borderRadius: '18px',
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} sx={{ color: HOUSEKEEPING_ACCENT }} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ ...cardSx, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(HOUSEKEEPING_ACCENT, isDark ? 0.16 : 0.14),
                color: isDark ? HOUSEKEEPING_ACCENT : '#0f766e',
              }}
            >
              <ChecklistRtlIcon />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: colors.text, fontSize: '1.05rem' }}>
                Cleaning checklist
              </Typography>
              <Typography sx={{ color: colors.subtleText, fontSize: '0.82rem' }}>
                Mark individual steps as required. Only required items block Mark Clean.
              </Typography>
            </Box>
          </Box>
          <Chip
            label={statusLabel}
            sx={{
              fontWeight: 700,
              bgcolor: enabled
                ? alpha(HOUSEKEEPING_ACCENT, isDark ? 0.2 : 0.18)
                : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'),
              color: enabled ? (isDark ? HOUSEKEEPING_ACCENT : '#0f766e') : colors.subtleText,
            }}
          />
        </Box>
      </Paper>

      <Paper sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <PowerSettingsNewIcon sx={{ fontSize: 18, color: HOUSEKEEPING_ACCENT }} />
              <Typography sx={{ fontWeight: 800, color: colors.text }}>Enable checklist</Typography>
            </Box>
            <Typography sx={{ color: colors.subtleText, fontSize: '0.82rem', maxWidth: 520 }}>
              Off by default. Add at least one checklist step, then turn this on to show it on room cleanings.
            </Typography>
          </Box>
          <Switch
            checked={enabled && items.length > 0}
            disabled={enabledSaving || items.length === 0}
            onChange={handleEnableToggle}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: HOUSEKEEPING_ACCENT },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: HOUSEKEEPING_ACCENT,
              },
            }}
          />
        </Box>
      </Paper>

      <Paper sx={cardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <InsightsIcon sx={{ fontSize: 18, color: HOUSEKEEPING_ACCENT }} />
          <Typography sx={{ fontWeight: 800, color: colors.text }}>Usage analytics</Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.25,
            mb: topUsed.length ? 1.5 : 0,
          }}
        >
          {[
            { label: 'Cleans with checklist', value: items.length ? (usage.cleans_with_checks || 0) : 0 },
            { label: 'Total item checks', value: usageRows.reduce((sum, row) => sum + row.usageCount, 0) },
            { label: 'Most used item', value: topUsed[0] ? `${topUsed[0].label} · ${topUsed[0].usageCount}×` : '—' },
          ].map((stat) => (
            <Box
              key={stat.label}
              sx={{
                p: 1.25,
                borderRadius: '14px',
                bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.03)',
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: colors.subtleText, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {stat.label}
              </Typography>
              <Typography sx={{ mt: 0.4, fontWeight: 800, color: colors.text, fontSize: '0.95rem' }}>
                {stat.value}
              </Typography>
            </Box>
          ))}
        </Box>
        {topUsed.length ? (
          <Typography sx={{ color: colors.subtleText, fontSize: '0.78rem' }}>
            Top usage: {topUsed.map((item) => `${item.label} ${item.usageCount}×`).join(' · ')}
          </Typography>
        ) : (
          <Typography sx={{ color: colors.subtleText, fontSize: '0.78rem' }}>
            Usage appears after staff check items while marking rooms clean.
          </Typography>
        )}
      </Paper>

      <Paper sx={cardSx}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, color: colors.text }}>Checklist items</Typography>
            <Typography sx={{ color: colors.subtleText, fontSize: '0.8rem' }}>
              {items.length} step{items.length === 1 ? '' : 's'} · {requiredCount} required · changes save automatically
              {saving ? ' · Saving…' : ''}
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={addStarterPack}
            sx={{ textTransform: 'none', fontWeight: 700, color: isDark ? HOUSEKEEPING_ACCENT : '#0f766e' }}
          >
            Add starter pack
          </Button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          {CATEGORIES.map((category) => {
            const Icon = category.icon;
            const categoryItems = items.filter((item) => item.category === category.id);
            return (
              <Box
                key={category.id}
                sx={{
                  p: 1.5,
                  borderRadius: '16px',
                  bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.03)',
                  minHeight: 220,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Icon sx={{ fontSize: 18, color: HOUSEKEEPING_ACCENT }} />
                  <Typography sx={{ fontWeight: 800, color: colors.text, fontSize: '0.88rem' }}>
                    {category.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={categoryItems.length}
                    sx={{ height: 20, ml: 'auto', fontWeight: 700 }}
                  />
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 1.25 }}>
                  {categoryItems.length === 0 ? (
                    <Typography sx={{ color: colors.subtleText, fontSize: '0.78rem', py: 1.5 }}>
                      No steps yet. Add one below.
                    </Typography>
                  ) : (
                    categoryItems.map((item) => {
                      const globalIndex = items.findIndex((row) => row.id === item.id);
                      const used = itemUsageCount(item, usage);
                      return (
                        <Box
                          key={item.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.65,
                            borderRadius: '12px',
                            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'}`,
                          }}
                        >
                          <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.82rem', color: colors.text }}>
                            {item.label}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${used}× used`}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              bgcolor: used
                                ? alpha(HOUSEKEEPING_ACCENT, isDark ? 0.2 : 0.16)
                                : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'),
                              color: used
                                ? (isDark ? HOUSEKEEPING_ACCENT : '#0f766e')
                                : colors.subtleText,
                            }}
                          />
                          <Chip
                            size="small"
                            label={item.mandatory ? 'Required' : 'Optional'}
                            onClick={() => toggleMandatory(item.id)}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              cursor: 'pointer',
                              bgcolor: item.mandatory
                                ? alpha('#f59e0b', isDark ? 0.22 : 0.16)
                                : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.06)'),
                              color: item.mandatory
                                ? (isDark ? '#fbbf24' : '#b45309')
                                : colors.subtleText,
                            }}
                          />
                          <IconButton size="small" onClick={() => moveItem(item.id, -1)} disabled={globalIndex <= 0}>
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => moveItem(item.id, 1)}
                            disabled={globalIndex === items.length - 1}
                          >
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => removeItem(item.id)}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={`Add ${category.label.toLowerCase()} step`}
                    value={drafts[category.id]}
                    onChange={(event) => setDrafts((prev) => ({ ...prev, [category.id]: event.target.value }))}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        addItem(category.id, drafts[category.id]);
                      }
                    }}
                    sx={getSoftFieldSx(isDark)}
                  />
                  <IconButton
                    onClick={() => addItem(category.id, drafts[category.id])}
                    sx={{
                      bgcolor: alpha(HOUSEKEEPING_ACCENT, 0.16),
                      color: isDark ? HOUSEKEEPING_ACCENT : '#0f766e',
                      '&:hover': { bgcolor: alpha(HOUSEKEEPING_ACCENT, 0.24) },
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>

      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3200}
        onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
          variant="filled"
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
